import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { ThematicMeetingAgendaEditor } from './ThematicMeetingAgendaEditor';

import type { GeneratedAgenda } from '@/components/MeetingAgendaBuilder/agendaViewModel';
import type { PlanNavigationState } from '@/pages/ThematicMeetings/utils/planNavigationState';
import type { ThematicMeetingAgenda as ThematicMeetingAgendaData } from '@/types/thematic-meetings';

import { useUpdateThematicMeetingAgenda } from '@/api/thematic-meetings';
import { AgendaAiBanner } from '@/components/AgendaAiBanner';
import { AiUnavailableNotice } from '@/components/AiUnavailableNotice';
import {
    mergeGeneratedAgenda,
    projectAgenda,
} from '@/components/MeetingAgendaBuilder/agendaViewModel';
import {
    AgendaPromptModal,
    type AgendaIntegrationMode,
    type AgendaPromptModalRef,
} from '@/components/MeetingAgendaBuilder/components/AgendaPromptModal';
import { PlanCard } from '@/components/MeetingPlan/PlanSection';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { useNavigate } from '@/hooks/utils/useNavigate';
import { AutosaveStatus } from '@/pages/ThematicMeetings/Plan/components/AutosaveStatus';
import { useAutosaveStatus } from '@/pages/ThematicMeetings/Plan/hooks/useAutosaveStatus';
import { useThematicMeetingAgendaGeneration } from '@/pages/ThematicMeetings/Plan/hooks/useThematicMeetingAgendaGeneration';
import { toContentLanguage } from '@/utils/contentLanguage';
import { htmlToPlainText } from '@/utils/htmlSanitizer';

interface ThematicMeetingAgendaProps {
    meetingId: string;
    agenda: ThematicMeetingAgendaData;
    /** The meeting's context, as stored rich text: it prefills the context the AI modal asks for. */
    context?: string | null;
    disabled?: boolean;
    dataTestId?: string;
}

type CreationGeneration = 'idle' | 'pending' | 'failed';

/**
 * Agenda of a thematic meeting: the shared agenda editor wired to this feature's own endpoints. The
 * agenda starts seeded from the meeting's template. "Generate the plan" opens the AI modal of the
 * post-review meeting: the manager gives an objective and a context, then adds the generated agenda to
 * the current one or replaces it.
 *
 * A completed meeting is a record: no AI banner, nothing to type into, and empty zones say so.
 */
export const ThematicMeetingAgenda = ({
    meetingId,
    agenda,
    context,
    disabled = false,
    dataTestId = 'thematic-meeting-agenda',
}: ThematicMeetingAgendaProps) => {
    const { t } = useAmelioTranslations();
    // The whole feature reads its language from `useSelectedAppLanguage`; the agenda does too, so
    // one page can never render its agenda in a language different from everything around it.
    const language = toContentLanguage(useSelectedAppLanguage());

    const { mutate: updateAgenda, isPending: isSaving } = useUpdateThematicMeetingAgenda();
    const { lastSavedAt, hasFailed, saveCallbacks } = useAutosaveStatus();
    const { generate, generateOrReport } = useThematicMeetingAgendaGeneration(meetingId);
    const promptModalRef = useRef<AgendaPromptModalRef>(null);
    const [creationGeneration, setCreationGeneration] = useState<CreationGeneration>('idle');
    const [editorVersion, setEditorVersion] = useState(0);
    const location = useLocation<PlanNavigationState | undefined>();
    const navigate = useNavigate();
    const hasConsumedGenerateRequestRef = useRef(false);

    // The generated agenda is merged into the agenda as saved. Once the merge is saved, a fresh editor
    // mounts on it: whatever an editor still held open belonged to the agenda it replaced.
    const integrate = (mode: AgendaIntegrationMode, generated: GeneratedAgenda) => {
        updateAgenda(
            {
                id: meetingId,
                request: mergeGeneratedAgenda(
                    mode,
                    generated,
                    projectAgenda(agenda, language),
                    language,
                ),
            },
            {
                ...saveCallbacks,
                onSuccess: () => {
                    saveCallbacks.onSuccess();
                    setEditorVersion((version) => version + 1);
                },
            },
        );
    };

    // Read through a reference by the effect below: `integrate` is new on every render, and the effect
    // depends only on what decides whether a generation is requested.
    const integrateRef = useRef(integrate);
    useEffect(() => {
        integrateRef.current = integrate;
    });

    // Opened right after its creation with a context: the agenda is generated once from that context,
    // and the request is dropped from the history entry so coming back to the page never starts it
    // again. Only the template's agenda is there to replace, so the manager is not asked.
    //
    // The request is written by the host's router, which took the navigation out of the creation
    // window; dropping it goes back the same way, through `useNavigate`. Moving the module's own
    // history instead leaves the entry untouched once mounted in the host, and the request fires
    // again on every return to the page — replacing an agenda the manager has since written.
    useEffect(() => {
        if (disabled || hasConsumedGenerateRequestRef.current || !location.state?.generateAgenda) {
            return;
        }
        hasConsumedGenerateRequestRef.current = true;
        navigate({ path: `${location.pathname}${location.search}`, replace: true });

        setCreationGeneration('pending');
        void generate('', '').then((generated) => {
            if (!generated) {
                setCreationGeneration('failed');
                return;
            }
            setCreationGeneration('idle');
            integrateRef.current('replace', generated);
        });
    }, [disabled, generate, navigate, location]);

    const openPromptModal = () => {
        setCreationGeneration('idle');
        promptModalRef.current?.open(['', htmlToPlainText(context ?? '')]);
    };

    const aiBanner = disabled ? null : (
        <>
            <AgendaAiBanner
                onGenerate={openPromptModal}
                isGenerating={creationGeneration === 'pending'}
            />

            {/* Always mounted, for its status region to be there before the failure is announced. */}
            <div className={creationGeneration === 'failed' ? 'mt-3' : undefined}>
                <AiUnavailableNotice
                    isUnavailable={creationGeneration === 'failed'}
                    message={t('thematicMeetings.plan.agendaGeneration.unavailable')}
                    retryLabel={t('thematicMeetings.plan.agendaGeneration.retry')}
                    onRetry={openPromptModal}
                    dataTestId={'thematic-meeting-agenda-generation-unavailable'}
                />
            </div>
        </>
    );

    return (
        <PlanCard dataTestId={dataTestId}>
            <ThematicMeetingAgendaEditor
                key={`${language}-${editorVersion}`}
                agenda={agenda}
                language={language}
                disabled={disabled}
                isLocked={creationGeneration === 'pending'}
                onSave={(request) => updateAgenda({ id: meetingId, request }, saveCallbacks)}
                banner={aiBanner}
            />

            <div className={'mt-3'}>
                <AutosaveStatus
                    isSaving={isSaving}
                    lastSavedAt={lastSavedAt}
                    hasFailed={hasFailed}
                    language={language}
                    dataTestId={`${dataTestId}-saved-indicator`}
                />
            </div>

            {!disabled && (
                <AgendaPromptModal
                    ref={promptModalRef}
                    onGenerate={generateOrReport}
                    onIntegrate={integrate}
                    canAdd={agenda.items.length > 0}
                />
            )}
        </PlanCard>
    );
};
